import React from 'react';
import { Drawer, Box, Typography, IconButton, Divider, CircularProgress, Button, Tooltip, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { getNote, NoteDetails } from '../../services/notesService';

export interface NotePreviewDrawerProps {
  open: boolean;
  noteId: string | null;
  onClose: () => void;
}

/**
 * Lekka podgląd notatki po prawej stronie — do szybkiego zerknięcia z kafla „Notatki”
 * na dashboardzie. Pełny widok dalej dostępny przez „Otwórz w module notatek”.
 */
export default function NotePreviewDrawer({ open, noteId, onClose }: NotePreviewDrawerProps) {
  const [loading, setLoading] = React.useState<boolean>(false);
  const [note, setNote] = React.useState<NoteDetails | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open || !noteId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNote(null);
    (async () => {
      try {
        const n = await getNote(noteId);
        if (!cancelled) setNote(n);
      } catch (e: any) {
        if (!cancelled) setError(e?.response?.data?.message || e?.message || 'Nie udało się wczytać notatki');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, noteId]);

  const formatDate = (iso: string | null) => {
    if (!iso) return '';
    try {
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return '';
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    } catch { return ''; }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      ModalProps={{ keepMounted: false }}
      PaperProps={{
        sx: {
          width: 640,
          maxWidth: '100vw',
          top: '52px',
          height: 'calc(100vh - 52px)',
          borderRadius: 0,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, borderBottom: '1px solid #eee' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 16, fontWeight: 500, color: '#2D2D32', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {note?.title || 'Notatka'}
          </Typography>
          {note?.dateIso && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {formatDate(note.dateIso)}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {note?.id && (
            <Tooltip title="Otwórz w module notatek">
              <IconButton
                component="a"
                href={`/app/notes?note=${encodeURIComponent(note.id)}`}
                target="_blank"
                rel="noopener"
                size="small"
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton size="small" onClick={onClose} aria-label="Zamknij podgląd">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 3, py: 2 }}>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
            <CircularProgress size={20} />
            <Typography variant="body2">Wczytywanie notatki…</Typography>
          </Box>
        )}
        {!loading && error && (
          <Typography color="error" variant="body2">{error}</Typography>
        )}
        {!loading && !error && note && (
          <>
            {Array.isArray(note.participants) && note.participants.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                {note.participants.slice(0, 12).map((p, idx) => (
                  <Chip key={`${p}-${idx}`} size="small" label={p} sx={{ bgcolor: '#F5F5FF', color: '#2D2D32', height: 22, borderRadius: '4px', '& .MuiChip-label': { px: 1, fontSize: 11 } }} />
                ))}
              </Box>
            )}
            <Divider sx={{ mb: 2 }} />
            <Box
              component="pre"
              sx={{
                m: 0,
                fontFamily: 'Roboto, sans-serif',
                fontSize: 14,
                color: '#2D2D32',
                lineHeight: 1.55,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              }}
            >
              {note.content || 'Brak treści notatki.'}
            </Box>
          </>
        )}
        {!loading && !error && !note && (
          <Typography variant="body2" color="text.secondary">Nie znaleziono notatki.</Typography>
        )}
      </Box>

      <Box sx={{ px: 3, py: 1.5, borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Zamknij</Button>
      </Box>
    </Drawer>
  );
}
